import React, { useRef } from "react";
import { useAppSelector, useAppDispatch } from "../../../../redux/store";
import { useSnackbar } from "notistack";
import { Box, IconButton, Tooltip, CircularProgress } from "@mui/material";
import {
  PhotoCamera,
  Person as PersonIcon,
} from "@mui/icons-material";

import {
  updateProfileImage,
  // deleteProfileImage,
  resetErrors,
} from "../../../../redux/slices/profileSlice";

const ProfileImageUpload = ({ size = 120 }: { size?: number }) => {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Redux state
  const { user } = useAppSelector((state) => state.login);
  const {
    profileImage,
    imageUploading,
    imageDeleting,
    imageError,
    imageDeleteError,
    imageDeleteSuccess,
    updateSuccess,
  } = useAppSelector((state) => state.profile);

  // Handle file selection
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      enqueueSnackbar("Please select a valid image file", { variant: "error" });
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      enqueueSnackbar("Image size should be less than 5MB", {
        variant: "error",
      });
      return;
    }

    // Upload the image
    if (user?.id) {
      dispatch(
        updateProfileImage({
          imageFile: file,
          userId: user.id,
        })
      );
    } else {
      enqueueSnackbar("User ID not found", { variant: "error" });
    }

    // Clear the input
    event.target.value = "";
  };

  // Handle image deletion
  // const handleDeleteImage = () => {
  //   if (user?.id || user?._id) {
  //     dispatch(deleteProfileImage(user.id || user._id));
  //   } else {
  //     enqueueSnackbar("User ID not found", { variant: "error" });
  //   }
  // };

  // Handle upload button click
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  // Success/Error notifications
  React.useEffect(() => {
    if (updateSuccess && !imageUploading) {
      enqueueSnackbar("Profile image updated successfully!", {
        variant: "success",
      });
    }
  }, [updateSuccess, imageUploading, enqueueSnackbar]);

  React.useEffect(() => {
    if (imageDeleteSuccess && !imageDeleting) {
      enqueueSnackbar("Profile image deleted successfully!", {
        variant: "success",
      });
    }
  }, [imageDeleteSuccess, imageDeleting, enqueueSnackbar]);

  React.useEffect(() => {
    if (imageError) {
      enqueueSnackbar(`Upload error: ${imageError}`, { variant: "error" });
      dispatch(resetErrors());
    }
  }, [imageError, enqueueSnackbar, dispatch]);

  React.useEffect(() => {
    if (imageDeleteError) {
      enqueueSnackbar(`Delete error: ${imageDeleteError}`, {
        variant: "error",
      });
      dispatch(resetErrors());
    }
  }, [imageDeleteError, enqueueSnackbar, dispatch]);

  // Determine the image source
  const getImageSrc = () => {
    if (profileImage) return profileImage;
    if (user?.photo) return user.photo;
    return null;
  };

  const imageSrc = getImageSrc();
  const isLoading = imageUploading || imageDeleting;

  return (
    <Box sx={{ position: "relative", display: "inline-block" }}>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        style={{ display: "none" }}
      />

      {/* Avatar with loading overlay */}
      <Box sx={{ position: "relative" }}>
        <Box
          sx={{
            width: size,
            height: size,
            // border: "4px solid",
            // borderColor: "background.paper",
            // boxShadow: 3,
            borderRadius: "50%",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "grey.100",
            backgroundImage: imageSrc ? `url(${imageSrc})` : "none",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        >
          {!imageSrc && (
            <PersonIcon sx={{ fontSize: size / 2, color: "grey.500" }} />
          )}
        </Box>

        {/* Loading overlay */}
        {isLoading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              borderRadius: "50%", // Match the container's circle
            }}
          >
            <CircularProgress size={size / 3} sx={{ color: "white" }} />
          </Box>
        )}
      </Box>

      {/* Action buttons */}
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          right: 0,
          display: "flex",
          gap: 0.5,
        }}
      >
        {/* Upload button */}
        <Tooltip title="Upload new photo">
          <IconButton
            onClick={handleUploadClick}
            disabled={isLoading}
            sx={{
              backgroundColor: "primary.main",
              color: "white",
              width: 36,
              height: 36,
              borderRadius: "50%",
              m: 1,
              "&:hover": {
                backgroundColor: "primary.dark",
              },
              "&:disabled": {
                backgroundColor: "grey.400",
              },
            }}
          >
            <PhotoCamera sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>

        {/* Delete button (only show if there's an image) */}
        {/* {imageSrc && (
          <Tooltip title="Delete photo">
            <IconButton
              onClick={handleDeleteImage}
              disabled={isLoading}
              sx={{
                backgroundColor: "error.main",
                color: "white",
                width: 36,
                height: 36,
                "&:hover": {
                  backgroundColor: "error.dark",
                },
                "&:disabled": {
                  backgroundColor: "grey.400",
                },
              }}
            >
              <DeleteIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )} */}
      </Box>
    </Box>
  );
};

export default ProfileImageUpload;
